export interface Account {
  id: string;
  email: string;
  createdAt: number;
  lastLoginAt: number;
  // M13 — incremented to invalidate ALL outstanding JWTs for this account.
  // Issued tokens carry the value at issue-time; verifySession rejects any
  // token whose `tv` payload doesn't match the current account.token_version.
  tokenVersion: number;
}

export interface CharacterStats {
  str: number;
  dex: number;
  con: number;
  int: number;
  fai: number;
  rap: number;
}

export const STARTING_STATS: CharacterStats = {
  str: 10,
  dex: 10,
  con: 10,
  int: 10,
  fai: 10,
  rap: 10,
};

export interface Position {
  x: number;
  y: number;
}

export type Facing = "N" | "S" | "E" | "W";

export const STARTING_ZONE_ID = "eldoria";
export const STARTING_POSITION: Position = { x: 16, y: 16 };

export const TILE_W = 64;
export const TILE_H = 32;
export const MOVE_COOLDOWN_MS = 125;
export const MOVE_COOLDOWN_MS_DIAG = Math.round(MOVE_COOLDOWN_MS * Math.SQRT2);
export const LOCAL_CHAT_RADIUS = 12;
export const RENDER_DELAY_MS = 100;
export const CHAT_MAX_LEN = 200;
export const CHAT_RATE_LIMIT_MS = 1500;
export const SERVER_TICK_HZ = 20;
export const SERVER_TICK_MS = 1000 / SERVER_TICK_HZ;
// M6.1 — how long a player stays in world after their WS closes before the
// tick-loop sweeps them out. Reconnecting within this window restores the
// exact runtime state (target / inCombat / cooldowns).
export const RECONNECT_GRACE_MS = 60_000;

export type PlayerLifeState = "alive" | "dead";

export interface PlayerSnapshot {
  id: string;
  name: string;
  x: number;
  y: number;
  facing: Facing;
  hpPct: number;
  // M8.x — mana ratio for the unified mana pool (0..1). Surfaced for party
  // HUD bars; not used by combat. Sent for every snapshotted player, not
  // just party members, so the client can resolve party members by id
  // without an extra broadcast channel.
  manaPct: number;
  lifeState: PlayerLifeState;
  // M6.1 — present and true while the WS is gone but the player is still in
  // world during the reconnect grace window. Omitted (undefined) otherwise to
  // keep the wire payload minimal.
  disconnected?: true;
}

export type MobAiState =
  | "idle"
  | "patrolling"
  | "chasing"
  | "attacking"
  | "leashing"
  | "dead";

export interface MobSnapshot {
  id: string;
  templateId: string;
  x: number;
  y: number;
  facing: Facing;
  hpPct: number;
  state: MobAiState;
  // Current movement speed multiplier from active slow debuffs (1.0 = no
  // slow, 0.5 = Frost Shock). Omitted from the wire when 1.0 to keep the
  // payload minimal; the client treats undefined as 1.0.
  slowMult?: number;
  // M17.2 — serverTime until which this mob is stunned (Stunning Blow).
  // Omitted when not stunned. The client shows a stun indicator + freezes
  // the mob's lerp while active.
  stunnedUntil?: number;
  // M17.4 — exact HP, shown by the client only to players who have unlocked
  // Scholar's Eye (INT 50). Cheap (2 numbers/mob); the client gates display.
  hp?: number;
  maxHp?: number;
}

// Per-mob debug fields, sent only to players who toggled the F3 debug
// overlay (the client opts in via `setMobDebug`). Used to render mob
// internals (state, swing timer, LOS misses, current target). Kept in
// a separate payload so the regular `worldSnapshot` stays lean for
// the 99% of players who don't have the overlay on.
export interface MobDebugSnapshot {
  id: string;
  losMissCount: number;
  swingAt: number; // absolute serverTime of next allowed swing
  targetCharacterId: string | null;
}

export interface ProjectileSnapshot {
  id: string;
  kind: import("./data/projectiles.js").ProjectileKind;
  x: number;
  y: number;
}

// M5.1: static NPC sprite descriptor. NPCs don't ride the 20Hz worldSnapshot —
// they're sent once per zone via the `npcsInZone` private payload on enterWorld
// / teleport. Template data (name, dialog, vendor stock) lives in
// shared/src/data/npcs.ts and is looked up by templateId on the client.
export interface NpcSnapshot {
  npcId: string;
  templateId: string;
  x: number;
  y: number;
  facing: Facing;
}

export type ChatChannel = "global" | "local" | "system";

export interface ChatMessage {
  id: string;
  channel: ChatChannel;
  senderCharacterId: string | null;
  senderName: string;
  text: string;
  timestamp: number;
}

export interface Character {
  id: string;
  accountId: string;
  name: string;
  level: number;
  xp: number;
  unspentPoints: number;
  stats: CharacterStats;
  hp: number;
  // M8 — single mana pool (was: manaArc + manaDiv). DB column renamed via
  // migrate(); old characters get manaArc+manaDiv summed into mana.
  mana: number;
  // M9 — persisted per-character max pools. Snapshotted at each level-up
  // from current CON / max(INT,FAI) via the game-config formula. Stat
  // allocations between levels bank for the next level — they don't
  // retroactively grow the pool.
  maxHpBase: number;
  maxManaBase: number;
  // M17.2 — the 8 banked accumulators (procs + regen fuel). Grown at each
  // level-up by 75/25 invested-only stat pairs; persisted as banked_json.
  banked: import("./data/banked.js").BankedAccumulators;
  zoneId: string;
  position: Position;
  facing: Facing;
  copper: number; // total wealth in copper; decompose for display via formatCurrency
  inventory: import("./data/items.js").ItemInstance[];
  equipped: EquippedSlots;
  hotbar: import("./data/spells.js").HotbarSlots; // 8 slots, spellId or null
  questLog: import("./data/quests.js").QuestLogEntry[]; // M5.2 — accepted + completed quests
  createdAt: number;
  updatedAt: number;
}

export type EquippedSlots = Record<
  import("./data/items.js").EquipSlot,
  import("./data/items.js").ItemInstance | null
>;

export function emptyEquippedSlots(): EquippedSlots {
  return {
    Head: null,
    Neck: null,
    Shoulders: null,
    Chest: null,
    Waist: null,
    Legs: null,
    Feet: null,
    RightHand: null,
    LeftHand: null,
    Ring1: null,
    Ring2: null,
    Cloak: null,
    Wrist1: null,
    Wrist2: null,
  };
}

export function deriveMaxHp(stats: CharacterStats): number {
  return 50 + 5 * stats.con;
}

// M8 — single mana pool. Uses max(INT, FAI) so both archetypes (INT-mage,
// FAI-priest) get equivalent baseline capacity. Hybrids don't get bonus
// (that would over-reward dual-stat builds); they just gain access to more
// spell schools, which is its own reward.
export function deriveMaxMana(stats: CharacterStats): number {
  return 20 + 5 * Math.max(stats.int, stats.fai);
}

export function deriveCarryCapacity(stats: CharacterStats): number {
  return 50 + 5 * stats.str;
}

export const CHARACTER_NAME_REGEX = /^[A-Za-z0-9]{3,16}$/;
